import { useEffect, useMemo, useState } from "react";
import { AreaChart, Area, XAxis, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowDownLeft, TrendingUp, Star } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const monthKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-ZA", { month: "long", year: "numeric" });
};

function Trend({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/50 bg-card p-4 shadow-sm">
      <p className="overline-label text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-2xl font-extrabold text-primary">{value}</p>
    </div>
  );
}

export default function WorkerWallet() {
  const [d, setD] = useState(null);
  const [txns, setTxns] = useState(null);
  const [month, setMonth] = useState("all");

  useEffect(() => {
    api.get("/worker/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/worker/transactions").then((r) => setTxns(r.data.filter((t) => t.status === "success"))).catch(() => setTxns([]));
  }, []);

  const months = useMemo(() => {
    if (!txns) return [];
    return [...new Set(txns.map((t) => monthKey(t.created_at)))].sort().reverse();
  }, [txns]);

  const filtered = useMemo(() => {
    if (!txns) return [];
    return month === "all" ? txns : txns.filter((t) => monthKey(t.created_at) === month);
  }, [txns, month]);

  const monthTotal = filtered.reduce((s, t) => s + (t.amount || 0), 0);

  if (!d || !txns) return <div className="space-y-4"><Skeleton className="h-28 w-full rounded-2xl" /><Skeleton className="h-40 w-full rounded-2xl" /></div>;

  return (
    <div className="space-y-5 animate-fade-up" data-testid="worker-wallet">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-brand-navy to-[#0a1526] p-6 text-white shadow-xl">
        <div className="grain absolute inset-0 opacity-[0.08]" />
        <p className="relative overline-label text-brand-cyan">Wallet balance</p>
        <p className="relative mt-1 font-heading text-4xl font-extrabold" data-testid="wallet-balance">R {d.wallet_balance.toFixed(2)}</p>
        <div className="relative mt-3 flex items-center gap-4 text-sm text-white/70">
          <span className="flex items-center gap-1"><TrendingUp className="h-4 w-4" /> {d.tip_count} tips</span>
          <span className="flex items-center gap-1"><Star className="h-4 w-4 fill-brand-cyan text-brand-cyan" /> {d.rating_avg || "New"}</span>
        </div>
      </div>

      <div>
        <p className="mb-2 font-heading font-bold">Trends</p>
        <div className="grid grid-cols-3 gap-3">
          <Trend label="Daily" value={`R${d.earnings.daily.toFixed(0)}`} />
          <Trend label="Weekly" value={`R${d.earnings.weekly.toFixed(0)}`} />
          <Trend label="Monthly" value={`R${d.earnings.monthly.toFixed(0)}`} />
        </div>
      </div>

      <div className="rounded-2xl border border-border/50 bg-card p-5 shadow-sm">
        <p className="font-heading font-bold">Last 7 days</p>
        <div className="mt-3 h-40">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.chart} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
              <defs>
                <linearGradient id="wg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00A3E0" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#00A3E0" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#94A3B8" }} />
              <Tooltip formatter={(v) => [`R${v}`, "Tips"]} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", color: "hsl(var(--foreground))" }} />
              <Area type="monotone" dataKey="amount" stroke="#00A3E0" strokeWidth={2.5} fill="url(#wg)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="font-heading font-bold">Transaction history</p>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-44" data-testid="month-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {month !== "all" && (
          <p className="mb-3 text-sm text-muted-foreground" data-testid="month-total">{monthLabel(month)}: <span className="font-semibold text-primary">R{monthTotal.toFixed(2)}</span> from {filtered.length} tips</p>
        )}
        <div className="space-y-2">
          {filtered.length === 0 && <p className="rounded-2xl border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground" data-testid="txns-empty">No tips in this period yet.</p>}
          {filtered.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.4) }}
              className="flex items-center gap-3 rounded-xl border border-border/50 bg-card p-4 shadow-sm" data-testid="txn-item">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 text-primary"><ArrowDownLeft className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">Tip received</p>
                <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-ZA")}</p>
                {(t.rating > 0 || t.emoji) && (
                  <div className="mt-1 flex items-center gap-2" data-testid="txn-rating">
                    {t.rating > 0 && (
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star key={s} className={`h-3.5 w-3.5 ${s <= t.rating ? "fill-brand-orange text-brand-orange" : "text-slate-300"}`} />
                        ))}
                      </span>
                    )}
                    {t.emoji && <span className="text-base leading-none" data-testid="txn-emoji">{t.emoji}</span>}
                  </div>
                )}
              </div>
              <div className="text-right">
                <p className="font-heading font-bold text-primary" data-testid="txn-net">+R{(t.net != null ? t.net : (t.amount || 0)).toFixed(2)}</p>
                {t.fee > 0 && (
                  <p className="text-[11px] text-muted-foreground" data-testid="txn-fee">R{(t.gross != null ? t.gross : (t.amount || 0)).toFixed(2)} tip − R{t.fee.toFixed(2)} fee</p>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
